import { QueryReturnValue } from '@reduxjs/toolkit/dist/query/baseQueryTypes';

import {
  Endpoints,
  GetOwnerPetsErrors,
  GetOwnerPremiumSubscriptionPlansErrors,
  GetOwnerServicesErrors,
  GetOwnerWellnessSubscriptionsErrors,
  PostOwnerErrors,
  PostOwnerPetsErrors,
  PremiumPlan,
  PutOwnerAddressErrors,
  PutOwnerPremiumSubErrors,
  service,
  WALK_TYPE_ID_DROP_INS,
  WALK_TYPE_ID_SITTING,
  WALK_TYPE_ID_TRAINING,
  WALK_TYPE_ID_WALK,
} from '../../../constants';
import { wagApi } from '../../../types/wagapi';
import {
  ApiError,
  buildApiErrorPipeline,
  mapErrorByType,
  mapErrorToApiSlice,
  unwrapSliceError,
  createErrorForV6,
  createErrorForV7,
} from '../../../utils/buildApiErrorPipeline';
import { tryCatch } from '../../../utils/tryCatch';
import { apiSlice } from '../../apiSlice';

export const ownersApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    postRegisterOwner: builder.mutation<
      wagApi.V6.PostRegisterOwnerResponse,
      wagApi.V6.PostRegisterOwnerPayload
    >({
      queryFn: async (body, _api, _extraOptions, query) => {
        const adjustedArgs = {
          url: Endpoints.V6.PostRegisterOwner,
          method: 'POST',
          body,
        };
        const [response] = await tryCatch(() => query(adjustedArgs));
        const { error } = response as QueryReturnValue;

        if (error) {
          return buildApiErrorPipeline(error as ApiError)
            .pipe(
              unwrapSliceError,
              createErrorForV6,
              mapErrorByType(PostOwnerErrors),
              mapErrorToApiSlice,
            ) as any;
          // TODO - Narrow down the typing definition of `buildApiErrorPipeline`
        }
        return response;
      },
    }),

    getOwnerWellnessSubscriptions: builder.query<
      wagApi.V6.GetOwnerWellnessSubscriptionsResponse,
      wagApi.V6.GetOwnerWellnessSubscriptionsPayload
    >({
      queryFn: async (params, _api, _extraOptions, query) => {
        /**
         * TODO: Support pagination if really needed
         * The number of subscriptions is also the maximum
         * number of pets with subscriptions we can display on the page
         * including the existing pets without subscriptions. Otherwise,
         * they it will be treated as "no subscription record".
         */
        const {
          ownerIdentifier,
          perPage = 20,
          ...restParams
        } = params;

        const url = Endpoints.V6.GetOwnerWellnessSubscriptions.replace('{{ownerId}}', params.ownerIdentifier);
        const adjustedArgs = {
          url,
          method: 'GET',
          params: {
            perPage,
            ...restParams,
          },
        };
        const [response] = await tryCatch(() => query(adjustedArgs));

        if (response.error) {
          return buildApiErrorPipeline(response.error)
            .pipe(
              unwrapSliceError,
              createErrorForV6,
              mapErrorByType(GetOwnerWellnessSubscriptionsErrors),
              mapErrorToApiSlice,
            );
        }
        return response;
      },
    }),
    getOwnerWalkServices: builder.query<
      wagApi.V6.GetOwnerServicesResponse,
      wagApi.V6.GetOwnerServicesPayload
    >({
      queryFn: async (params, _api, _extraOptions, query) => {
        const {
          ownerIdentifier,
          category,
          ...restParams
        } = params;

        const url = Endpoints.V6.GetOwnerServices.replace('{{ownerId}}', params.ownerIdentifier);
        const adjustedArgs = {
          url,
          method: 'GET',
          params: {
            ...restParams,
            /**
             * This is a specialized query to only load the walks
             * We have to categorized them so we can "reduce" them properly
             * Create a new specialized query for other categories if needed
             */
            category: service[WALK_TYPE_ID_WALK].category,
          },
        };
        const [response] = await tryCatch(() => query(adjustedArgs));

        if (response.error) {
          return buildApiErrorPipeline(response.error)
            .pipe(
              unwrapSliceError,
              createErrorForV6,
              mapErrorByType(GetOwnerServicesErrors),
              mapErrorToApiSlice,
            );
        }
        return response;
      },
    }),

    getOwnerTrainingServices: builder.query<
      wagApi.V6.GetOwnerServicesResponse,
      wagApi.V6.GetOwnerServicesPayload
    >({
      queryFn: async (params, _api, _extraOptions, query) => {
        const {
          ownerIdentifier,
          category,
          ...restParams
        } = params;

        const url = Endpoints.V6.GetOwnerServices.replace('{{ownerId}}', params.ownerIdentifier);
        const adjustedArgs = {
          url,
          method: 'GET',
          params: {
            ...restParams,
            /**
             * This is a specialized query to only load the walks
             * We have to categorized them so we can "reduce" them properly
             * Create a new specialized query for other categories if needed
             */
            category: service[WALK_TYPE_ID_TRAINING].category,
          },
        };
        const [response] = await tryCatch(() => query(adjustedArgs));

        if (response.error) {
          return buildApiErrorPipeline(response.error)
            .pipe(
              unwrapSliceError,
              createErrorForV6,
              mapErrorByType(GetOwnerServicesErrors),
              mapErrorToApiSlice,
            );
        }
        return response;
      },
    }),

    /**
     * Handles both sitting & boarding queries
     */
    getOwnerSittingServices: builder.query<
      wagApi.V6.GetOwnerServicesResponse,
      wagApi.V6.GetOwnerServicesPayload
    >({
      queryFn: async (params, _api, _extraOptions, query) => {
        const {
          ownerIdentifier,
          category,
          ...restParams
        } = params;

        const url = Endpoints.V6.GetOwnerServices.replace('{{ownerId}}', params.ownerIdentifier);
        const adjustedArgs = {
          url,
          method: 'GET',
          params: {
            ...restParams,
            /**
             * This is a specialized query to only load the walks
             * We have to categorized them so we can "reduce" them properly
             * Create a new specialized query for other categories if needed
             */
            category: service[WALK_TYPE_ID_SITTING].category,
          },
        };
        const [response] = await tryCatch(() => query(adjustedArgs));

        if (response.error) {
          return buildApiErrorPipeline(response.error)
            .pipe(
              unwrapSliceError,
              createErrorForV6,
              mapErrorByType(GetOwnerServicesErrors),
              mapErrorToApiSlice,
            );
        }
        return response;
      },
    }),

    getOwnerDropInServices: builder.query<
      wagApi.V6.GetOwnerServicesResponse,
      wagApi.V6.GetOwnerServicesPayload
    >({
      queryFn: async (params, _api, _extraOptions, query) => {
        const {
          ownerIdentifier,
          category,
          ...restParams
        } = params;

        const url = Endpoints.V6.GetOwnerServices.replace('{{ownerId}}', params.ownerIdentifier);
        const adjustedArgs = {
          url,
          method: 'GET',
          params: {
            ...restParams,
            /**
             * This is a specialized query to only load the walks
             * We have to categorized them so we can "reduce" them properly
             * Create a new specialized query for other categories if needed
             */
            category: service[WALK_TYPE_ID_DROP_INS].category,
          },
        };
        const [response] = await tryCatch(() => query(adjustedArgs));

        if (response.error) {
          return buildApiErrorPipeline(response.error)
            .pipe(
              unwrapSliceError,
              createErrorForV6,
              mapErrorByType(GetOwnerServicesErrors),
              mapErrorToApiSlice,
            );
        }
        return response;
      },
    }),

    putOwnerAddress: builder.mutation<
      wagApi.V6.PutOwnerAddressResponse,
      wagApi.V6.PutOwnerAddressPayload
    >({
      queryFn: async (body, _api, _extraOptions, query) => {
        const {
          ownerIdentifier,
          ...restBody
        } = body;

        const url = Endpoints.V6.PutOwnerUpdateAddress.replace('{{ownerId}}', ownerIdentifier);

        const adjustedArgs = {
          url,
          method: 'PUT',
          body: restBody,
        };

        const [response] = await tryCatch(() => query(adjustedArgs));
        const { error } = response as QueryReturnValue;

        if (error) {
          return buildApiErrorPipeline(error as ApiError)
            .pipe(
              unwrapSliceError,
              createErrorForV6,
              mapErrorByType(PutOwnerAddressErrors),
              mapErrorToApiSlice,
            );
        }
        return response;
      },
    }),

    getPremiumSubscriptionPlans: builder.query<
      wagApi.V6.GetPremiumSubscriptionPlansResponse,
      wagApi.V6.GetPremiumSubscriptionPlansPayload,
    >({
      queryFn: async (body, _api, _extraOptions, query) => {
        const {
          ownerId,
        } = body;

        const url = Endpoints.V6.GetPremiumSubscriptionPlans.replace('{{ownerId}}', ownerId);

        const adjustedArgs = {
          url,
          method: 'GET',
        };

        const [response] = await tryCatch(() => query(adjustedArgs));

        if (response.error) {
          return buildApiErrorPipeline(response.error)
            .pipe(
              unwrapSliceError,
              createErrorForV6,
              mapErrorByType(GetOwnerPremiumSubscriptionPlansErrors),
              mapErrorToApiSlice,
            );
        }

        return response;
      },
    }),

    putSubscribeToPremium: builder.mutation<
      wagApi.V6.PutSubscribeToPremiumResponse,
      // No need to pass a payload because this function will already set it
      wagApi.V6.PutSubscribeToPremiumPayload
    >({
      queryFn: async (body, _api, _extraOptions, query) => {
        const {
          ownerIdentifier,
          planIdentifier,
        } = body;
        const url = Endpoints.V6.PutSubscribeToPremium.replace('{{ownerIdentifier}}', ownerIdentifier.toString());
        const adjustedArgs = {
          url,
          method: 'PUT',
          body: {
            planIdentifier,
          },
        };
        const [response] = await tryCatch(() => query(adjustedArgs));

        if (response.error) {
          return buildApiErrorPipeline(response.error)
            .pipe(
              unwrapSliceError,
              createErrorForV7,
              mapErrorByType(PutOwnerPremiumSubErrors),
              mapErrorToApiSlice,
            );
        }

        return response;
      },
    }),

    postOwnerPets: builder.mutation<
      wagApi.V6.PostOwnerPetsResponse,
      wagApi.V6.PostOwnerPetsPayload
    >({
      queryFn: async (body, _api, _extraOptions, query) => {
        const {
          ownerIdentifier,
          ...restBody
        } = body;

        const url = Endpoints.V6.PostOwnerPets.replace('{{ownerId}}', ownerIdentifier);

        const adjustedArgs = {
          url,
          method: 'POST',
          body: restBody,
        };

        const [response] = await tryCatch(() => query(adjustedArgs));
        const { error } = response as QueryReturnValue;

        if (error) {
          return buildApiErrorPipeline(error as ApiError)
            .pipe(
              unwrapSliceError,
              createErrorForV6,
              mapErrorByType(PostOwnerPetsErrors),
              mapErrorToApiSlice,
            );
        }
        return response;
      },
    }),

    getOwnerPets: builder.query<
      wagApi.V6.GetOwnerPetsResponse,
      wagApi.V6.GetOwnerPetsPayload
    >({
      queryFn: async (params, _api, _extraOptions, query) => {
        const {
          ownerIdentifier,
        } = params;

        const url = Endpoints.V6.GetOwnerPets.replace('{{ownerId}}', ownerIdentifier);
        const adjustedArgs = {
          url,
          method: 'GET',
          params,
        };

        const [response] = await tryCatch(() => query(adjustedArgs));

        if (response.error) {
          return buildApiErrorPipeline(response.error)
            .pipe(
              unwrapSliceError,
              createErrorForV6,
              mapErrorByType(GetOwnerPetsErrors),
              mapErrorToApiSlice,
            );
        }
        return response;
      },
    }),
  }),
  overrideExisting: false,
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  usePostRegisterOwnerMutation,
  useGetOwnerWellnessSubscriptionsQuery,
  useLazyGetOwnerWellnessSubscriptionsQuery,
  useGetOwnerWalkServicesQuery,
  useGetOwnerTrainingServicesQuery,
  useGetPremiumSubscriptionPlansQuery,
  usePutSubscribeToPremiumMutation,
  useGetOwnerSittingServicesQuery,
  useGetOwnerDropInServicesQuery,
  usePutOwnerAddressMutation,
  usePostOwnerPetsMutation,
  useGetOwnerPetsQuery,
} = ownersApi;
